import { _ } from "../polyglot/polyglot.js";
import { htmlCanvas, Widget } from "@foretagsplatsen/widgetjs";

/**
 * A modal dialog widget.
 * The modal will not be rendered on the document until its .show() method is called and the widget will take
 * care of removing the modal from the document whenever it is disposed or before re-rendering it.
 *
 * @example
 *        var modal = new ModalWidget({
 *			title: title || ('Help'),
 *			content: function(html) {
 *				html.p('Well hello there, stranger.');
 *			},
 *			severity: 'info'
 *		});
 *
 *        modal.show();
 */
export default class ModalWidget extends Widget {
	constructor(spec = {}) {
		super(...arguments);
		this._title = spec.title || "";
		this._category = spec.category;
		this._cssClass = spec.cssClass;
		this._content = spec.content || function () {};
		this._severity = spec.severity || "info";
	}

	show() {
		this._removeModalFromDocument();
		this.renderOn(htmlCanvas("body"));
		this.asJQuery().modal();
	}

	renderContentOn(html) {
		html.div(
			{ class: ["modal-dialog", this._cssClass] },
			html.div(
				{ class: "modal-content" },
				html.div(
					{ class: "modal-header" },
					html.button(
						{
							type: "button",
							class: "close",
							"data-dismiss": "modal",
							"aria-hidden": "true",
						},
						"×",
					),
					this._renderCategoryOn.bind(this),
					this._renderTitleOn.bind(this),
				),
				html.div(
					{ class: "modal-body" },
					this._renderBodyOn.bind(this),
				),
				html.div(
					{ class: "modal-footer" },
					this._renderFooterOn.bind(this),
				),
			),
		);

		this.asJQuery().on(
			"hidden.bs.modal",
			this._removeModalFromDocument.bind(this),
		);
	}

	willDetach() {
		this._removeModalFromDocument();
	}

	_renderRootOn(html) {
		// Overriding root so that these attributes can be set on the root div
		return html
			.div({
				class: [`${this._severity}-modal`, "modal", "fade"],
				tabindex: "-1",
				role: "dialog",
				"aria-labelledby": `${this.getId()}-label`,
				"aria-hidden": "true",
			})
			.id(this.getId());
	}

	_renderTitleOn(html) {
		html.h3(
			{
				class: "modal-title",
				id: `${this.getId()}-label`,
			},
			this._title,
		);
	}

	_renderCategoryOn(html) {
		if (!this._category) {
			return;
		}

		html.h4(
			{
				class: "modal-title",
			},
			this._category,
		);
	}

	_renderBodyOn(html) {
		html.div(this._content);
	}

	_renderFooterOn(html) {
		html.a(
			{
				class: "btn btn-primary",
				"data-dismiss": "modal",
				"data-test": "close",
			},
			_("Close"),
		);
	}

	_removeModalFromDocument() {
		if (!this.isRendered()) {
			return;
		}
		this.asJQuery().modal("hide").remove();
	}
}
